import { Injectable } from '@angular/core';

import { ChannelContextModel, DiagnosticVerbosity, SignageChangeModel, SignageChangeKind } from '@cubigo/digital-signage';

const VERBOSITY: readonly string[] = Object.values(DiagnosticVerbosity);

@Injectable({ providedIn: 'root' })
export class SignageChangeParserService {
  public parse(message: unknown, context: ChannelContextModel, priorityChannel: boolean): SignageChangeModel | null {
    if (message === null || typeof message !== 'object' || Array.isArray(message)) {
      return null;
    }

    const value = message as Record<string, unknown>;

    if (!this.matchesChannel(value, context)) {
      return null;
    }

    switch (value['kind']) {
      case SignageChangeKind.Configuration:
      case SignageChangeKind.Content:
      case SignageChangeKind.PriorityNews:
        return { kind: value['kind'] };
      case SignageChangeKind.Diagnostics:
        return this.diagnosticsChange(value['verbosity']);
      case SignageChangeKind.Live:
        return this.liveChange(value['live'], value['videoId']);
      default:
        return this.legacyChange(value, priorityChannel);
    }
  }

  private matchesChannel(value: Record<string, unknown>, context: ChannelContextModel): boolean {
    return (value['providerChannelId'] === undefined || value['providerChannelId'] === context.id)
      && (value['providerUuid'] === undefined || value['providerUuid'] === context.providerUuid);
  }

  private legacyChange(value: Record<string, unknown>, priorityChannel: boolean): SignageChangeModel | null {
    if (value['kind'] !== undefined) {
      return null;
    }

    if (typeof value['isYouTubeChannelLive'] === 'boolean') {
      return this.liveChange(value['isYouTubeChannelLive'], value['youTubeVideoId']);
    }

    // ItemPublished is an invalidation; its resident/news payload is never retained.
    return priorityChannel ? { kind: SignageChangeKind.PriorityNews } : null;
  }

  private diagnosticsChange(verbosity: unknown): SignageChangeModel | null {
    if (!VERBOSITY.includes(verbosity as string)) {
      return null;
    }

    return { kind: SignageChangeKind.Diagnostics, verbosity: verbosity as DiagnosticVerbosity };
  }

  private liveChange(live: unknown, videoId: unknown): SignageChangeModel | null {
    if (live === false) {
      return { kind: SignageChangeKind.Live, live: false };
    }

    if (live === true && typeof videoId === 'string' && videoId.trim().length > 0) {
      return { kind: SignageChangeKind.Live, live: true, videoId };
    }

    return null;
  }
}
